import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { collection, doc, getDoc, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { Plus, Trash2 } from "lucide-react";
import { AGREEMENT_FREQUENCIES, FREQUENCY_LABEL, computeTotals, paths, type AgreementFrequency, type LineItem, type ServiceAgreement } from "@clevis/shared-types";
import { Button, Card, CardTitle, ConfirmButton, Input, Label, Select, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call, CallError } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { useDepartments } from "../../hooks/useDepartments";
import { useJobTypes } from "../../hooks/useJobTypes";
import { useSalesSettings, useTeamMembers } from "../../hooks/useSalesSettings";
import { CustomerPicker } from "../../components/CustomerPicker";
import { textareaClass } from "../../components/JobEditCard";
import { SearchBox, useListView } from "../../components/ListControls";

// Maintenance agreements: a customer's plan, billed on its own every month
// to every year (a draft to review, or emailed), with maintenance visits
// opened as jobs two weeks before they're due.

type Row = ServiceAgreement & { id: string };
const money = (c: number) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDay = (ms: number | null) => (ms ? new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }) : "—");
const toDateInput = (ms: number | null) => (ms ? new Date(ms).toISOString().slice(0, 10) : "");
const fromDateInput = (s: string) => (s ? Date.parse(`${s}T00:00:00Z`) : null);
const todayInput = () => new Date().toISOString().slice(0, 10);
const errText = (err: unknown) => {
  const e = err as CallError;
  return e.hint ? `${e.message} ${e.hint}` : e.message;
};
const STATUS_STYLE: Record<Row["status"], string> = { active: "bg-success/15 text-success", paused: "bg-brandorange/15 text-brandorange-dark", canceled: "bg-steel/10 text-steel line-through" };

export function AgreementsPage() {
  const { tenantId } = useAuth();
  const [, navigate] = useLocation();
  const [rows, setRows] = useState<Row[]>([]);
  const [status, setStatus] = useState<"active" | "paused" | "canceled" | "all">("active");
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ row?: Row; customerId?: string } | null>(() => {
    const c = new URLSearchParams(window.location.search).get("customer");
    return c ? { customerId: c } : null;
  });
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(query(collection(db, paths.tenantServiceAgreements(tenantId)), orderBy("createdAt", "desc")), (s) => setRows(s.docs.map((d) => ({ id: d.id, ...(d.data() as ServiceAgreement) }))));
  }, [tenantId]);

  const view = useListView(rows, {
    text: (r) => [r.customerName, r.name, FREQUENCY_LABEL[r.frequency], r.status],
    sorts: { next: (a, b) => (a.nextBillAt ?? Infinity) - (b.nextBillAt ?? Infinity), customer: (a, b) => a.customerName.localeCompare(b.customerName) },
    defaultSort: "next",
    filter: (r) => status === "all" || r.status === status,
    pageSize: 50,
  });
  const monthly = useMemo(() => rows.filter((r) => r.status === "active").reduce((s, r) => s + r.totalCents / ({ monthly: 1, quarterly: 3, semiannual: 6, annual: 12 }[r.frequency]), 0), [rows]);

  if (!tenantId) return null;
  if (editing) {
    return (
      <AgreementEditor
        tenantId={tenantId}
        row={editing.row}
        customerId={editing.customerId}
        onDone={(msg) => {
          setEditing(null);
          if (window.location.search) navigate("/agreements", { replace: true });
          if (msg) setNote(msg);
        }}
      />
    );
  }
  const current = open ? rows.find((r) => r.id === open) : null;
  if (current) return <AgreementDetail tenantId={tenantId} row={current} onBack={() => setOpen(null)} onEdit={() => setEditing({ row: current })} />;

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-bold">Maintenance agreements</h1>
          {monthly > 0 ? <p className="text-xs text-steel">{money(Math.round(monthly))} a month in active agreements</p> : null}
        </div>
        <Button onClick={() => setEditing({})}>
          <Plus size={14} className="mr-1 inline" /> New agreement
        </Button>
      </div>
      {note ? <p className="rounded bg-success/10 px-2 py-1 text-xs text-ink">{note}</p> : null}
      <div className="flex flex-wrap items-center gap-2">
        <SearchBox value={view.query} onChange={view.setQuery} placeholder="Search customer or plan…" />
        <Select className="w-36" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="canceled">Canceled</option>
          <option value="all">All</option>
        </Select>
      </div>
      <Card className="p-0">
        {view.page.map((r) => (
          <button key={r.id} className="flex h-10 w-full items-center gap-3 border-b border-steel/10 px-4 text-left text-sm last:border-0 hover:bg-cream/50" onClick={() => setOpen(r.id)}>
            <span className="min-w-0 flex-1 truncate text-ink">
              {r.customerName} <span className="text-xs text-steel">· {r.name}</span>
            </span>
            <span className="hidden w-28 text-xs text-steel sm:inline">{FREQUENCY_LABEL[r.frequency]}</span>
            <span className="w-20 text-right text-xs text-ink">{money(r.totalCents)}</span>
            <span className="hidden w-32 text-right text-xs text-steel sm:inline">{r.status === "active" ? (r.nextBillAt ? `next ${fmtDay(r.nextBillAt)}` : "ended") : ""}</span>
            <span className={cn("w-20 shrink-0 rounded-full px-2 py-0.5 text-center text-xs", STATUS_STYLE[r.status])}>{r.status}</span>
            {r.lastError ? <span className="text-xs text-danger" title={r.lastError}>!</span> : null}
          </button>
        ))}
        {view.filtered.length === 0 ? <p className="px-4 py-8 text-center text-sm text-steel">{rows.length ? "Nothing matches." : "No maintenance agreements yet. Start one here or from a customer."}</p> : null}
      </Card>
    </div>
  );
}

function AgreementDetail({ tenantId, row, onBack, onEdit }: { tenantId: string; row: Row; onBack: () => void; onEdit: () => void }) {
  const dept = useDepartments();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  async function act(fn: string, data: Record<string, unknown>, done: string) {
    setBusy(true);
    setError("");
    try {
      await call(fn, { tenantId, agreementId: row.id, ...data });
      setMsg(done);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="mx-auto max-w-3xl space-y-3">
      <button className="text-xs text-steel hover:text-ink" onClick={onBack}>
        ← Maintenance agreements
      </button>
      <Card className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h1 className="text-lg font-bold text-ink">{row.name}</h1>
            <p className="text-sm text-steel">
              <Link href={`/customers/${row.customerId}`} className="text-brandorange-dark hover:underline">
                {row.customerName}
              </Link>
              {row.departmentId ? ` · ${dept.departments.find((d) => d.id === row.departmentId)?.name ?? ""}` : ""}
              {row.salespersonName ? ` · sold by ${row.salespersonName}` : ""}
            </p>
          </div>
          <span className={cn("rounded-full px-2.5 py-0.5 text-xs", STATUS_STYLE[row.status])}>{row.status}</span>
        </div>
        <div className="grid gap-2 text-sm sm:grid-cols-3">
          <div>
            <div className="text-xs text-steel">Billing</div>
            <div className="text-ink">
              {money(row.totalCents)} {FREQUENCY_LABEL[row.frequency].toLowerCase()}
            </div>
            <div className="text-xs text-steel">{row.billing === "send" ? `emailed${row.billingEmail ? ` to ${row.billingEmail}` : " to the customer"}` : "as a draft for review"}</div>
          </div>
          <div>
            <div className="text-xs text-steel">Next bill</div>
            <div className="text-ink">{row.status === "active" ? (row.nextBillAt ? fmtDay(row.nextBillAt) : "none (ended)") : "—"}</div>
            <div className="text-xs text-steel">
              started {fmtDay(row.startAt)}
              {row.endAt ? ` · ends ${fmtDay(row.endAt)}` : ""}
            </div>
          </div>
          <div>
            <div className="text-xs text-steel">Visits</div>
            <div className="text-ink">
              {row.visitsPerYear ? `${row.visitsPerYear} a year` : "none"}
              {row.discountBps ? <span className="ml-2 rounded-full bg-brandorange/15 px-1.5 py-0.5 text-[11px] font-semibold text-brandorange-dark">{(row.discountBps / 100).toString().replace(/\.0$/, "")}% off</span> : null}
            </div>
            {row.visitsPerYear && row.status === "active" ? <div className="text-xs text-steel">next due {fmtDay(row.nextVisitAt)} (opens as a job 2 weeks ahead)</div> : null}
          </div>
        </div>
        <div className="divide-y divide-steel/10 rounded-lg border border-steel/15 text-sm">
          {row.lines.map((l, i) => (
            <div key={i} className="flex justify-between gap-2 px-3 py-1.5">
              <span className="text-ink">
                {l.quantity !== 1 ? `${l.quantity} × ` : ""}
                {l.description}
              </span>
              <span className="text-ink">{money(Math.round(l.quantity * l.unitPriceCents))}</span>
            </div>
          ))}
        </div>
        {row.lastError ? <p className="rounded bg-danger/10 px-2 py-1 text-xs text-danger">{row.lastError} It's tried again tomorrow morning, or use Bill now.</p> : null}
        {row.terms ? <details className="text-xs text-steel"><summary className="cursor-pointer">Terms</summary><p className="mt-1 whitespace-pre-wrap">{row.terms}</p></details> : null}
        {msg ? <p className="rounded bg-success/10 px-2 py-1 text-xs text-ink">{msg}</p> : null}
        {error ? <p className="text-xs text-danger">{error}</p> : null}
        {row.status !== "canceled" ? (
          <div className="flex flex-wrap gap-2">
            <Button onClick={onEdit}>Edit</Button>
            {row.status === "active" && row.nextBillAt ? (
              <Button variant="secondary" busy={busy} onClick={() => void act("billServiceAgreementNow", {}, "Billed. Find the invoice under Invoices.")}>
                Bill now
              </Button>
            ) : null}
            <Button variant="ghost" busy={busy} onClick={() => void act("setServiceAgreementStatus", { action: row.status === "paused" ? "resume" : "pause" }, row.status === "paused" ? "Resumed." : "Paused: nothing is billed until you resume.")}>
              {row.status === "paused" ? "Resume" : "Pause"}
            </Button>
            <ConfirmButton className="px-2 py-1 text-xs" confirm={`Cancel ${row.name} for ${row.customerName}? No more bills or visits.`} onConfirm={() => act("setServiceAgreementStatus", { action: "cancel" }, "Canceled.")}>
              Cancel agreement
            </ConfirmButton>
          </div>
        ) : null}
        {row.history.length ? (
          <div>
            <div className="mb-1 text-xs font-semibold text-ink">History</div>
            {[...row.history].reverse().map((h) => (
              <div key={`${h.kind}${h.refId}`} className="flex gap-2 text-xs">
                <span className="w-24 text-steel">{fmtDay(h.at)}</span>
                {h.kind === "visit" ? (
                  <Link href={`/jobs/${h.refId}`} className="text-brandorange-dark hover:underline">
                    {h.label}
                  </Link>
                ) : (
                  <span className="text-ink">{h.label}</span>
                )}
              </div>
            ))}
          </div>
        ) : null}
      </Card>
    </div>
  );
}

type EditLine = Pick<LineItem, "description" | "quantity" | "unitPriceCents" | "taxable">;

function AgreementEditor({ tenantId, row, customerId: initialCustomer, onDone }: { tenantId: string; row?: Row; customerId?: string; onDone: (msg?: string) => void }) {
  const settings = useSalesSettings(tenantId);
  const dept = useDepartments();
  const jobTypes = useJobTypes(tenantId);
  const team = useTeamMembers(tenantId);
  const { user } = useAuth();
  const [customerId, setCustomerId] = useState(row?.customerId ?? initialCustomer ?? "");
  const [customerName, setCustomerName] = useState(row?.customerName ?? "");
  const [name, setName] = useState(row?.name ?? "");
  const [departmentId, setDepartmentId] = useState(row?.departmentId ?? "");
  const [frequency, setFrequency] = useState<AgreementFrequency>(row?.frequency ?? "monthly");
  const [start, setStart] = useState(row ? toDateInput(row.startAt) : todayInput());
  const [end, setEnd] = useState(toDateInput(row?.endAt ?? null));
  const [lines, setLines] = useState<EditLine[]>(row?.lines.map((l) => ({ description: l.description, quantity: l.quantity, unitPriceCents: l.unitPriceCents, taxable: l.taxable })) ?? [{ description: "", quantity: 1, unitPriceCents: 0, taxable: false }]);
  const [taxRate, setTaxRate] = useState(row ? String(row.taxRateBps / 100) : "0");
  const [billing, setBilling] = useState<"draft" | "send">(row?.billing ?? "draft");
  const [billingEmail, setBillingEmail] = useState(row?.billingEmail ?? "");
  const [visits, setVisits] = useState(String(row?.visitsPerYear ?? 0));
  const [discount, setDiscount] = useState(row?.discountBps ? String(row.discountBps / 100) : "");
  const [visitType, setVisitType] = useState(row?.visitJobType ?? "");
  const [owner, setOwner] = useState(row ? (row.salespersonUid ?? "") : (user?.uid ?? ""));
  // Clevis staff looking in ("Log in as") aren't on the team: start at Nobody.
  useEffect(() => {
    if (!row && team.length && owner && !team.some((m) => m.uid === owner)) setOwner("");
  }, [team, row, owner]);
  const [terms, setTerms] = useState(row?.terms ?? "");
  const [termsTouched, setTermsTouched] = useState(!!row);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // The customer's name for a link from their page; the department's terms until edited.
  useEffect(() => {
    if (customerId && !customerName) void getDoc(doc(db, paths.tenantCustomer(tenantId, customerId))).then((s) => setCustomerName((s.get("displayName") as string) ?? ""));
  }, [tenantId, customerId, customerName]);
  useEffect(() => {
    if (termsTouched) return;
    setTerms(settings.agreementTerms[departmentId] ?? settings.agreementTerms[""] ?? "");
  }, [departmentId, settings.agreementTerms, termsTouched]);

  const valid = lines.filter((l) => l.description.trim());
  const totals = computeTotals(valid.map((l) => ({ ...l, unitCostCents: null, itemId: null, priceVisibility: "show" as const, components: null, showComponents: true })), Math.round(parseFloat(taxRate || "0") * 100));
  const setLine = (i: number, patch: Partial<EditLine>) => setLines((ls) => ls.map((l, n) => (n === i ? { ...l, ...patch } : l)));

  async function save() {
    if (!customerId) return setError("Pick the customer.");
    if (!name.trim()) return setError("Name the plan (Annual door care, Monthly gate service…).");
    if (!valid.length) return setError("Add what's billed each period.");
    const startAt = fromDateInput(start);
    if (startAt === null) return setError("Pick the first billing date.");
    setBusy(true);
    setError("");
    try {
      await call("saveServiceAgreement", {
        tenantId,
        ...(row ? { agreementId: row.id } : {}),
        customerId,
        name: name.trim(),
        departmentId: departmentId || null,
        terms,
        lines: valid.map((l) => ({ ...l, quantity: Number(l.quantity) || 1, unitCostCents: null, itemId: null, priceVisibility: "show", components: null, showComponents: true })),
        taxRateBps: Math.round(parseFloat(taxRate || "0") * 100),
        frequency,
        billing,
        billingEmail,
        startAt,
        endAt: fromDateInput(end),
        visitsPerYear: Math.max(0, Math.min(12, parseInt(visits || "0", 10) || 0)),
        discountBps: Math.max(0, Math.min(10000, Math.round(parseFloat(discount || "0") * 100) || 0)),
        visitJobType: visitType || null,
        salespersonUid: owner || null,
      });
      onDone(row ? "Saved." : `${name.trim()} set up for ${customerName || "the customer"}. The first bill goes out ${fmtDay(startAt)} (each morning's run).`);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl">
      <Card className="space-y-3">
        <CardTitle className="mb-0">{row ? `Edit ${row.name}` : "New maintenance agreement"}</CardTitle>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Customer</Label>
            {row ? (
              <p className="py-2 text-sm text-ink">{row.customerName}</p>
            ) : (
              <CustomerPicker tenantId={tenantId} value={customerId} valueName={customerName} onChange={(c) => { setCustomerId(c?.id ?? ""); setCustomerName(c?.displayName ?? ""); }} />
            )}
          </div>
          <div>
            <Label>Plan name</Label>
            <Input value={name} placeholder="Annual garage door maintenance" maxLength={120} onChange={(e) => setName(e.target.value)} />
          </div>
          {dept.departments.length ? (
            <div>
              <Label>Department</Label>
              <Select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                <option value="">No department</option>
                {dept.departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
          <div>
            <Label>Sold by</Label>
            <Select value={owner} onChange={(e) => setOwner(e.target.value)}>
              <option value="">Nobody</option>
              {team.map((m) => (
                <option key={m.uid} value={m.uid}>
                  {m.name}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div>
          <Label>Billed each period</Label>
          {lines.map((l, i) => (
            <div key={i} className="mt-1 grid grid-cols-[1fr_4.5rem_6rem_3rem_1.5rem] items-center gap-2">
              <Input value={l.description} placeholder="Maintenance plan, 2 doors" onChange={(e) => setLine(i, { description: e.target.value })} />
              <Input type="number" min="0.25" step="1" value={l.quantity} onChange={(e) => setLine(i, { quantity: parseFloat(e.target.value) || 0 })} aria-label="Quantity" />
              <Input type="number" min="0" step="0.01" value={(l.unitPriceCents / 100).toString()} onChange={(e) => setLine(i, { unitPriceCents: Math.round(parseFloat(e.target.value || "0") * 100) })} aria-label="Price" />
              <label className="flex items-center gap-1 text-[11px] text-steel">
                <input type="checkbox" checked={l.taxable} onChange={(e) => setLine(i, { taxable: e.target.checked })} /> tax
              </label>
              <button className="text-steel hover:text-danger" onClick={() => setLines((ls) => ls.filter((_, n) => n !== i))} aria-label="Remove line">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          <div className="mt-1 flex items-center justify-between">
            <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => setLines((ls) => [...ls, { description: "", quantity: 1, unitPriceCents: 0, taxable: false }])}>
              <Plus size={13} className="mr-1 inline" /> Line
            </Button>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-xs text-steel">Tax %</span>
              <Input className="w-20 py-1" type="number" step="0.01" min="0" max="50" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} />
              <span className="font-semibold text-ink">{money(totals.totalCents)} per period</span>
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <Label>How often</Label>
            <Select value={frequency} onChange={(e) => setFrequency(e.target.value as AgreementFrequency)}>
              {AGREEMENT_FREQUENCIES.map((f) => (
                <option key={f} value={f}>
                  {FREQUENCY_LABEL[f]}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label>First bill</Label>
            <Input type="date" value={start} disabled={!!row?.lastBilledAt} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div>
            <Label>Ends (optional)</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <Label>Each invoice</Label>
            <Select value={billing} onChange={(e) => setBilling(e.target.value as "draft" | "send")}>
              <option value="draft">Waits as a draft for us to review</option>
              <option value="send">Goes out by email on its own</option>
            </Select>
          </div>
          {billing === "send" ? (
            <div>
              <Label>Email to</Label>
              <Input value={billingEmail} placeholder="the customer's email" onChange={(e) => setBillingEmail(e.target.value)} />
            </div>
          ) : null}
          <div>
            <Label>Visits a year</Label>
            <Input type="number" min="0" max="12" value={visits} onChange={(e) => setVisits(e.target.value)} />
          </div>
          <div>
            <Label>Member discount</Label>
            <div className="flex items-center gap-1">
              <Input type="number" min="0" max="100" step="0.5" className="w-24" value={discount} placeholder="0" onChange={(e) => setDiscount(e.target.value)} />
              <span className="text-sm text-steel">% off their estimates and invoices while the plan is active</span>
            </div>
          </div>
          {parseInt(visits || "0", 10) > 0 ? (
            <div>
              <Label>Visit job type</Label>
              <Select value={visitType} onChange={(e) => setVisitType(e.target.value)}>
                <option value="">No type</option>
                {jobTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
        </div>

        <div>
          <Label>Terms (printed on every invoice it makes)</Label>
          <textarea className={textareaClass} rows={5} maxLength={20000} value={terms} placeholder="What's covered, what isn't, how to cancel…" onChange={(e) => { setTerms(e.target.value); setTermsTouched(true); }} />
          <p className="text-[11px] text-steel">Each department's standard terms are set under Estimates → Types &amp; terms.</p>
        </div>
        {error ? <p className="text-xs text-danger">{error}</p> : null}
        <div className="flex gap-2">
          <Button busy={busy} onClick={() => void save()}>
            {row ? "Save" : "Start agreement"}
          </Button>
          <Button variant="ghost" onClick={() => onDone()}>
            Cancel
          </Button>
        </div>
      </Card>
    </div>
  );
}

/** On a customer's page: their agreements, and a way to start one. */
export function CustomerAgreements({ tenantId, customerId }: { tenantId: string; customerId: string }) {
  const [rows, setRows] = useState<Row[] | null>(null);
  useEffect(
    () =>
      onSnapshot(
        query(collection(db, paths.tenantServiceAgreements(tenantId)), where("customerId", "==", customerId)),
        (s) => setRows(s.docs.map((d) => ({ id: d.id, ...(d.data() as ServiceAgreement) })).filter((r) => r.status !== "canceled")),
        () => setRows(null),
      ),
    [tenantId, customerId],
  );
  if (rows === null) return null;
  return (
    <Card className="p-0">
      <div className="flex items-center justify-between px-4 pt-3">
        <CardTitle className="mb-0">Maintenance agreements ({rows.length})</CardTitle>
        <Link href={`/agreements?customer=${customerId}`} className="text-xs font-semibold text-brandorange-dark hover:underline">
          + New agreement
        </Link>
      </div>
      <div className="mt-2">
        {rows.map((r) => (
          <Link key={r.id} href="/agreements" className="flex justify-between gap-2 border-b border-steel/10 px-4 py-1.5 text-xs last:border-0 hover:bg-cream/50">
            <span className="text-ink">{r.name}</span>
            <span className="text-steel">
              {money(r.totalCents)} {FREQUENCY_LABEL[r.frequency].toLowerCase()} · {r.status === "active" ? (r.nextBillAt ? `next ${fmtDay(r.nextBillAt)}` : "ended") : r.status}
            </span>
          </Link>
        ))}
        {rows.length === 0 ? <p className="px-4 pb-3 text-xs text-steel">None yet. A plan bills on its own and opens its maintenance visits as jobs.</p> : null}
      </div>
    </Card>
  );
}
